import { useMemo, useState } from "react";
import {
  Badge,
  BadgeSize,
  BadgeVariant,
  Button,
  ButtonVariant,
  ButtonSize,
  Hide
} from "shared/components";
import { formatToCurrency } from "shared/utils";
import { CriteriaRentType, Money } from "shared/types";
import { EditApplicationRentModal } from "./EditApplicationRentModal";
import { useAuthContext } from "providers/Auth";
import { AdjustedApplicationRentActivity } from "shared/app/ActivityLog/types";

interface ApplicationRentSectionProps {
  applicationId: string;
  applicationRentAmount: {
    monthly_rent_amount?: Money;
    rent_amount?: Money;
    home_rent_amount?: Money;
    site_rent_amount?: Money;
    previous_rent_amount?: Money;
    housing_assistance_voucher_amount?: Money;
    pet_rent_amount?: Money;
  };
  onViewDetails: () => void;
  canEdit?: boolean;
  onSuccess?: () => void;
  latestApplicationRentHistory?: AdjustedApplicationRentActivity;
  criteriaRentType: CriteriaRentType;
}

export const ApplicationRentSection = ({
  applicationId,
  applicationRentAmount,
  onViewDetails,
  canEdit = true,
  onSuccess,
  latestApplicationRentHistory,
  criteriaRentType
}: ApplicationRentSectionProps) => {
  const [showSetRentModal, setShowSetRentModal] = useState(false);
  const { pms, type } = useAuthContext();
  const homeSiteRentEnabled = pms.isManageAmerica || type.isHomeSiteRentEnabled;

  const latestRentHistoryString = useMemo(() => {
    let result = "";
    if (homeSiteRentEnabled) {
      if (criteriaRentType !== CriteriaRentType.Site)
        result +=
          formatToCurrency(applicationRentAmount.home_rent_amount || null) +
          " home";
      if (criteriaRentType === CriteriaRentType.Both) result += " + ";
      if (criteriaRentType !== CriteriaRentType.Home)
        result +=
          formatToCurrency(applicationRentAmount.site_rent_amount || null) +
          " site";
    } else
      result +=
        formatToCurrency(applicationRentAmount.rent_amount || null) +
        " rent amount";

    if (applicationRentAmount.housing_assistance_voucher_amount?.cents) {
      result += " - ";
      result +=
        formatToCurrency(
          applicationRentAmount.housing_assistance_voucher_amount
        ) + " assistance";
    }

    if (applicationRentAmount.pet_rent_amount) {
      result += " + ";
      result +=
        formatToCurrency(applicationRentAmount.pet_rent_amount) + " pet";
    }

    return result;
  }, [
    latestApplicationRentHistory,
    applicationRentAmount,
    homeSiteRentEnabled
  ]);

  return (
    <div className="w-full flex items-center gap-4">
      <div className="flex-1">
        <div className="flex items-center gap-2 mb-2">
          <p className="text-xl font-bold text-content-1000">
            Application rent
          </p>
          <Hide if={!latestApplicationRentHistory}>
            <Badge variant={BadgeVariant.BASE} size={BadgeSize.SM}>
              Adjusted
            </Badge>
          </Hide>
        </div>
        <div className="flex items-center gap-1">
          <p className="text-base font-bold text-content-1000">
            {formatToCurrency(
              applicationRentAmount.monthly_rent_amount || null
            )}
          </p>
          <p className="text-base font-medium text-content-300 truncate">
            ({latestRentHistoryString})
          </p>
        </div>
        <Hide if={!latestApplicationRentHistory}>
          <Button
            variant={ButtonVariant.TEXT}
            size={ButtonSize.SM}
            onClick={onViewDetails}
            className="!text-primary-500 !p-0 !text-sm mt-2"
          >
            View details
          </Button>
        </Hide>
      </div>

      <Hide if={!canEdit}>
        <Button
          variant={ButtonVariant.BASE}
          size={ButtonSize.SM}
          onClick={() => setShowSetRentModal(true)}
        >
          Edit rent
        </Button>
      </Hide>

      <EditApplicationRentModal
        opened={showSetRentModal}
        onClose={() => setShowSetRentModal(false)}
        applicationId={applicationId}
        applicationRentAmount={applicationRentAmount}
        onSuccess={onSuccess}
        criteriaRentType={criteriaRentType}
      />
    </div>
  );
};
